import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import defaultContent from '../data/website_content.json';
import { ARTICLES_DATA } from '../data/articles_data';
import {
  WebsiteContent,
  JournalArticle,
  ProductUpdateRelease,
  PricingTier,
  FaqItem,
  BrandData,
  ContactData,
} from '../types/content';

interface ContentContextType {
  content: WebsiteContent;
  updateContent: (updates: Partial<WebsiteContent>) => void;
  // Journal / Blog management
  addJournalArticle: (article: JournalArticle) => void;
  updateJournalArticle: (id: string, updates: Partial<JournalArticle>) => void;
  deleteJournalArticle: (id: string) => void;
  // Product Updates / Changelog management
  addProductUpdate: (update: ProductUpdateRelease) => void;
  updateProductUpdate: (version: string, updates: Partial<ProductUpdateRelease>) => void;
  deleteProductUpdate: (version: string) => void;
  // Pricing management
  updatePricingTiers: (tiers: PricingTier[], baseSoloMonthlyPKR?: number) => void;
  // FAQ management
  updateFaqs: (faqs: FaqItem[]) => void;
  // Brand & Contact management
  updateBrandAndContact: (brand?: Partial<BrandData>, contact?: Partial<ContactData>) => void;
  // AI Gemini API Key
  geminiApiKey: string;
  setGeminiApiKey: (key: string) => void;
  // Backup & Restore
  exportContentJson: () => void;
  importContentJson: (jsonString: string) => { success: boolean; error?: string };
  resetToDefaultContent: () => void;
  hasCustomEdits: boolean;
}

const STORAGE_KEY = 'lashary_website_custom_content_v1.5';
const GEMINI_API_KEY_STORAGE = 'lashary_gemini_api_key';

const initialDefaultContent: WebsiteContent = {
  ...(defaultContent as unknown as WebsiteContent),
  journal: ARTICLES_DATA,
};

const ContentContext = createContext<ContentContextType | null>(null);

export const useContent = (): ContentContextType => {
  const context = useContext(ContentContext);
  if (!context) {
    throw new Error('useContent must be used within a ContentProvider');
  }
  return context;
};

export const ContentProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [content, setContent] = useState<WebsiteContent>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Ensure all 105+ articles are present even if previous cache had older mock items
        const savedJournal: JournalArticle[] = Array.isArray(parsed.journal) ? parsed.journal : [];
        const combinedJournal = [
          ...savedJournal,
          ...ARTICLES_DATA.filter((a) => !savedJournal.some((sj) => sj.id === a.id)),
        ];
        return {
          ...initialDefaultContent,
          ...parsed,
          journal: combinedJournal.length >= ARTICLES_DATA.length ? combinedJournal : ARTICLES_DATA,
        };
      }
    } catch {
      // ignore
    }
    return initialDefaultContent;
  });

  const [geminiApiKey, setGeminiApiKeyState] = useState<string>(() => {
    try {
      return (
        localStorage.getItem(GEMINI_API_KEY_STORAGE) ||
        'AQ.Ab8RN6LB2iDU3rLwZo4bDTeCS4HHbn8hFrw9JvE0ortLIobvWQ'
      );
    } catch {
      return 'AQ.Ab8RN6LB2iDU3rLwZo4bDTeCS4HHbn8hFrw9JvE0ortLIobvWQ';
    }
  });

  const [hasCustomEdits, setHasCustomEdits] = useState<boolean>(() => {
    try {
      return !!localStorage.getItem(STORAGE_KEY);
    } catch {
      return false;
    }
  });

  const persistContent = (newContent: WebsiteContent) => {
    setContent(newContent);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newContent, null, 2));
      setHasCustomEdits(true);
    } catch (e) {
      console.error('Failed to save website content to localStorage', e);
    }
  };

  const updateContent = (updates: Partial<WebsiteContent>) => {
    const updated = { ...content, ...updates };
    persistContent(updated);
  };

  // Journal Articles
  const addJournalArticle = (article: JournalArticle) => {
    const exists = content.journal.some((a) => a.id === article.id);
    const updatedList = exists
      ? content.journal.map((a) => (a.id === article.id ? article : a))
      : [article, ...content.journal];
    persistContent({ ...content, journal: updatedList });
  };

  const updateJournalArticle = (id: string, updates: Partial<JournalArticle>) => {
    const updatedList = content.journal.map((a) =>
      a.id === id ? ({ ...a, ...updates } as JournalArticle) : a
    );
    persistContent({ ...content, journal: updatedList });
  };

  const deleteJournalArticle = (id: string) => {
    const updatedList = content.journal.filter((a) => a.id !== id);
    persistContent({ ...content, journal: updatedList });
  };

  // Product Updates
  const addProductUpdate = (update: ProductUpdateRelease) => {
    const exists = content.productUpdates.some((u) => u.version === update.version);
    const updatedList = exists
      ? content.productUpdates.map((u) => (u.version === update.version ? update : u))
      : [update, ...content.productUpdates];
    persistContent({ ...content, productUpdates: updatedList });
  };

  const updateProductUpdate = (version: string, updates: Partial<ProductUpdateRelease>) => {
    const updatedList = content.productUpdates.map((u) =>
      u.version === version ? ({ ...u, ...updates } as ProductUpdateRelease) : u
    );
    persistContent({ ...content, productUpdates: updatedList });
  };

  const deleteProductUpdate = (version: string) => {
    const updatedList = content.productUpdates.filter((u) => u.version !== version);
    persistContent({ ...content, productUpdates: updatedList });
  };

  // Pricing
  const updatePricingTiers = (tiers: PricingTier[], baseSoloMonthlyPKR?: number) => {
    persistContent({
      ...content,
      pricing: {
        baseSoloMonthlyPKR: baseSoloMonthlyPKR ?? content.pricing.baseSoloMonthlyPKR,
        tiers,
      },
    });
  };

  // FAQs
  const updateFaqs = (faqs: FaqItem[]) => {
    persistContent({ ...content, faq: faqs });
  };

  // Brand & Contact
  const updateBrandAndContact = (brand?: Partial<BrandData>, contact?: Partial<ContactData>) => {
    persistContent({
      ...content,
      brand: brand ? { ...content.brand, ...brand } : content.brand,
      contact: contact ? { ...content.contact, ...contact } : content.contact,
    });
  };

  // Gemini API Key
  const setGeminiApiKey = (key: string) => {
    const trimmed = key.trim();
    setGeminiApiKeyState(trimmed);
    try {
      if (trimmed) {
        localStorage.setItem(GEMINI_API_KEY_STORAGE, trimmed);
      } else {
        localStorage.removeItem(GEMINI_API_KEY_STORAGE);
      }
    } catch {
      // ignore
    }
  };

  // Export as downloaded JSON file
  const exportContentJson = () => {
    try {
      const dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(content, null, 2));
      const downloadAnchor = document.createElement('a');
      downloadAnchor.setAttribute('href', dataStr);
      downloadAnchor.setAttribute('download', 'website_content.json');
      document.body.appendChild(downloadAnchor);
      downloadAnchor.click();
      downloadAnchor.remove();
    } catch (err) {
      console.error('Failed to export content JSON', err);
    }
  };

  // Import JSON file
  const importContentJson = (jsonString: string): { success: boolean; error?: string } => {
    try {
      const parsed = JSON.parse(jsonString);
      if (!parsed.brand || !parsed.pricing || !parsed.journal || !parsed.productUpdates) {
        return { success: false, error: 'JSON does not match the required Lashary website schema structure.' };
      }
      persistContent(parsed as WebsiteContent);
      return { success: true };
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Invalid JSON file format.';
      return { success: false, error: msg };
    }
  };

  // Reset to default
  const resetToDefaultContent = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
    setContent(initialDefaultContent);
    setHasCustomEdits(false);
  };

  return (
    <ContentContext.Provider
      value={{
        content,
        updateContent,
        addJournalArticle,
        updateJournalArticle,
        deleteJournalArticle,
        addProductUpdate,
        updateProductUpdate,
        deleteProductUpdate,
        updatePricingTiers,
        updateFaqs,
        updateBrandAndContact,
        geminiApiKey,
        setGeminiApiKey,
        exportContentJson,
        importContentJson,
        resetToDefaultContent,
        hasCustomEdits,
      }}
    >
      {children}
    </ContentContext.Provider>
  );
};

export default ContentProvider;
